import { Images, Calendar, MapPin } from 'lucide-react'
import { useLocation } from 'wouter'
import { getImageUrls } from '@/utils/imageUrls'
import defaultImage from '@/assets/default-image.svg'
import { LazyLoadImage } from 'react-lazy-load-image-component'

export interface Gallery {
  id: number | string
  heading: string
  page_title?: string
  content?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
}

interface GalleryCardProps {
  gallery: Gallery
}

export default function GalleryCard({ gallery }: GalleryCardProps) {
  const [, navigate] = useLocation()

  const handleClick = () => {
    navigate(`/galleries/${gallery.id}`)
  }

  const imageUrls = gallery.filename ? getImageUrls('galleries', gallery.filename) : null

  const formattedDate = gallery.published_date
    ? new Date(gallery.published_date).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    })
    : null

  return (
    <div
      onClick={handleClick}
      className="relative cursor-pointer group shadow-[0_4px_26px_0_rgba(88,88,88,0.3)] rounded-[12px] border border-[#464646] overflow-hidden bg-[#212124] h-full min-h-[254px]"
    >
      {imageUrls ? (
        <div className="relative w-full h-full">
          <LazyLoadImage
            src={imageUrls.landscapeThumb || defaultImage}
            alt={gallery.filename_alt || gallery.heading}
            effect="opacity"
            wrapperClassName="lazy-image-wrapper"
            className="
              w-full h-full object-cover
              transition-transform duration-300
              group-hover:scale-105
            "
          />
        </div>
      ) : (
        <div className="absolute inset-0 bg-[#212124] flex items-center justify-center">
          <img src={defaultImage} alt="Default News" />
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 text-white  flex flex-col justify-end h-full">
        <div className="relative mt-auto">
          <div
            className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/10 backdrop-blur-sm [mask-image:linear-gradient(to_top,black,rgba(0,0,0,0.85),rgba(0,0,0,0.4),transparent)]"
          />

          <div className="relative px-2 pt-12 pb-[8px]">
            <h3 className="font-semibold text-sm font-figtree line-clamp-2 leading-[16px]">
              {gallery.heading}
            </h3>

            <div className="flex items-center py-1 gap-4 text-white text-[12px] font-figtree font-medium justify-between leading-[14px]">
              {formattedDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span className='text-[10px] leading-[12px] font-medium font-figtree text-[#ffffff]'>{formattedDate}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
